// The fog-of-war shader (docs/31). One board-sized quad rendered to a texture at art resolution
// (one fragment per art pixel, ≤ 20 Hz — the weather lesson: a screen-resolution fbm quad starves
// swiftshader e2e) and drawn in the fog layer, over the lit terrain and under the units and the
// overlay camera. Output is premultiplied (Phaser's NORMAL blend: C + dst·(1−A)), so every step is
// "darken by d" or "lerp toward colour X by t".
//
// Masks (SUB texels per square, linear-filtered; see fog.ts `fogMasks`):
//   uA.r in sight now   uA.g explored or in sight   uA.b lit (a light falls there in the dark)
//   uB.r opaque ground  uB.g the focused hero's sight   uB.b depth of the unexplored dark (0 edge → 1 deep)
// Every mask feathers *outward* from its squares, and the domain warp only ever widens the clear
// region (max of the straight and the warped sample), so a live square is never fogged: the curl
// of the fog's edge happens inside the squares out of sight. Sight takes a third of the warp, so
// the clear area stays tight to its squares; memory's edge into the dark curls freely.
//
// Layers, bottom to top:
//   memory     explored, out of sight: a heavy cool desaturating wash and a strong darkening, so the
//              layout is still there but plainly not live
//   unexplored an opaque two-tone fog, deeper away from anything seen, drifting fbm with parallax;
//              nothing of the board shows through it
//   light      warm glow where a light falls inside the fog (a torch, lava); it thins memory, but
//              only tints the unexplored dark
//   focus      squares the focused hero cannot see (but the party can) get a light veil, so its own
//              sight reads as the clearest region
// There is no drawn edge to the sight: the step from clear to memory to dark is the edge (docs/31 J18).
export const FOG_FRAG = `
#pragma phaserTemplate(shaderName)
#pragma phaserTemplate(extensions)
#pragma phaserTemplate(features)
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
#pragma phaserTemplate(fragmentDefine)
varying vec2 outTexCoord;
#pragma phaserTemplate(outVariables)
#pragma phaserTemplate(fragmentHeader)
uniform sampler2D uA;
uniform sampler2D uB;
uniform vec2 uSize;       // board size in art pixels
uniform vec2 uCells;      // board size in squares
uniform float uTime;      // seconds (frozen under reduce motion)
uniform vec2 uDrift;      // drift direction (wind) and speed
uniform float uStretch;   // >1 streaks the fog along the wind
uniform vec3 uDeep;
uniform vec3 uWisp;
uniform vec3 uMem;
uniform vec3 uWarm;
uniform float uDense;     // unexplored coverage cap
uniform float uMemWash;   // memory veil: wash toward uMem
uniform float uMemDark;   // memory veil: darkening
uniform float uFocus;     // 1 while a hero is focused
uniform float uStrength;  // 1 full, lower in Soft (the gameplay fog itself never thins below ~0.8)

float hash(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) { v += a * noise(p); p = p * 2.03 + vec2(17.1, 9.2); a *= 0.5; }
  return v;
}
vec4 darken(vec4 acc, float d) { return vec4(acc.rgb * (1.0 - d), 1.0 - (1.0 - acc.a) * (1.0 - d)); }
vec4 toward(vec4 acc, vec3 x, float t) { return vec4(acc.rgb * (1.0 - t) + x * t, 1.0 - (1.0 - acc.a) * (1.0 - t)); }
// board position (squares, top-left origin) → mask texture coordinate (canvas textures are upright at outTexCoord)
vec2 tc(vec2 c) { return vec2(c.x / uCells.x, 1.0 - c.y / uCells.y); }

void main() {
  vec2 uv = vec2(outTexCoord.x, 1.0 - outTexCoord.y);
  vec2 px = floor(uv * uSize) + 0.5;            // the art pixel
  vec2 cell = px / uSize * uCells;              // board position in squares
  float t = uTime;
  vec2 sc = vec2(1.0 / uStretch, 1.0);
  vec2 drift = uDrift * t;

  // two fbm fields drifting at different speeds (parallax), and a warp that curls the edges
  float n1 = fbm(cell * 0.55 * sc - drift * 0.6);
  float n2 = fbm(cell * 1.25 * sc - drift * 1.3 + 9.0);
  float n3 = fbm(cell * 0.55 * sc - drift * 0.6 + vec2(5.2, 1.3));
  float n4 = fbm(cell * 1.7 * sc - drift * 1.6 + vec2(2.7, 8.1));
  vec2 warp = (vec2(n1, n3) - 0.5) * 1.1 + (vec2(n2, n4) - 0.5) * 0.45;   // big slow curls + small fast ones

  vec4 a0 = texture2D(uA, tc(cell));
  vec4 aw = texture2D(uA, tc(cell + warp));
  vec4 as = texture2D(uA, tc(cell + warp * 0.3));
  vec4 b0 = texture2D(uB, tc(cell));
  // sight curls only a little (a third of the warp): with no line drawn, a tight edge is the edge
  float clear = max(a0.r, as.r);
  float known = max(max(a0.g, aw.g), clear);
  float lit = max(a0.b, aw.b * 0.85);
  float unseen = 1.0 - clear;
  float unexpl = 1.0 - known;
  float depth = b0.b;
  float dens = smoothstep(0.22, 0.82, fbm(cell * 0.32 * sc - drift + n2 * 0.9));
  vec4 acc = vec4(0.0);

  // memory: explored, out of sight — a heavy cool wash and a strong darkening; the layout is still
  // there, but nothing about it reads as live. A light falling there thins it a little.
  float mem = unseen * (1.0 - lit * 0.3);
  acc = toward(acc, uMem, mem * uMemWash * (0.9 + 0.2 * (dens - 0.5)) * uStrength);
  acc = darken(acc, mem * uMemDark * uStrength);

  // unexplored: opaque, two-tone, deeper in the middle of the dark, lighter wisps drifting over it.
  // Only the very rim of the feather (where it meets memory) lets anything through.
  float deep = clamp(uDense * (0.97 + 0.03 * depth), 0.0, 1.0);
  vec3 col = mix(uDeep, uWisp, (0.06 + 0.16 * smoothstep(0.45, 0.8, n2) * (1.0 - 0.6 * depth)) * (0.6 + 0.4 * dens));
  acc = toward(acc, col, smoothstep(0.0, 0.7, unexpl) * deep);

  // light falling in the fog: a warm glow that breathes (it colours the dark, it doesn't open it)
  float flick = 0.82 + 0.18 * sin(t * 2.7 + n1 * 6.0);
  acc = toward(acc, uWarm, lit * unseen * 0.16 * flick * (0.6 + 0.4 * dens));

  // the focused hero's own sight reads clearest: a light veil on live squares it doesn't see
  float notMine = uFocus * clear * (1.0 - b0.g);
  acc = toward(acc, uMem, notMine * 0.16);
  acc = darken(acc, notMine * 0.16);

  gl_FragColor = acc;
}
`;
